"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect } from "react";
import { usePathname } from "next/navigation";
import { resolveUtmParamsForForwarding, withForwardedUtmParams } from "@/lib/url/forwardUtmParams";

/**
 * MXP-2873: forward campaign `utm_*` params onto every anchor pointing at
 * `cloud.pages.trimble.com`, wherever it lives in the page (TerraLink, rich
 * text, trimble-terra nav/footer, HTML-string renderers, raw `<a>`).
 *
 * This is deliberately a DOM-level decorator rather than a change to each
 * link component: there is no single shared link wrapper in this codebase,
 * and some anchors (trimble-terra `GlobalFooter`, `documentToHtmlString`
 * output) can't be reached from React props at all.
 *
 * Flow (matches Jaron / existing MXP trial attribution):
 *   1. Visitor lands on *.trimble.com with UTMs from ads/search/newsletters
 *   2. Those `utm_*` values are persisted in sessionStorage
 *   3. Every cloud.pages CTA carries the stored set — including after the
 *      visitor browses other pages where the URL no longer has UTMs
 *
 * Runs only on the client, after hydration, so CDN-cached HTML never carries
 * another visitor's UTMs. Rewrites the `href` attribute itself, so new-tab,
 * middle-click and Cmd/Ctrl-click all carry the UTMs without click interception.
 */

/** Marks an anchor whose href we have rewritten (value: "1"). */
const FORWARDED_ATTR = "data-mxp-utm-forwarded";
/** Holds the authored href so we can re-apply when stored UTMs change. */
const ORIGINAL_ATTR = "data-mxp-utm-original";
/** Cheap prefilter; `withForwardedUtmParams` still does the exact hostname check. */
const CANDIDATE_SELECTOR = 'a[href*="cloud.pages.trimble.com"]';
export default function UtmLinkDecorator() {
  const $ = _c(2);
  const pathname = usePathname();
  let t0;
  if ($[0] !== pathname) {
    t0 = [pathname];
    $[0] = pathname;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  useEffect(_temp2, t0);
  return null;
}
function _temp2() {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return;
  }
  const utm = resolveUtmParamsForForwarding(window.location.search);
  const hasUtm = Object.keys(utm).length > 0;
  const restore = _temp;
  const decorate = anchor_0 => {
    if (!hasUtm) {
      if (anchor_0.hasAttribute(FORWARDED_ATTR)) {
        restore(anchor_0);
      }
      return;
    }
    const authored = anchor_0.getAttribute(ORIGINAL_ATTR) ?? anchor_0.getAttribute("href");
    if (!authored) {
      return;
    }
    const next = withForwardedUtmParams(authored, utm);
    if (!next) {
      return;
    }
    if (anchor_0.getAttribute(FORWARDED_ATTR) === "1" && anchor_0.getAttribute("href") === next) {
      return;
    }
    if (!anchor_0.hasAttribute(ORIGINAL_ATTR)) {
      anchor_0.setAttribute(ORIGINAL_ATTR, authored);
    }
    anchor_0.setAttribute("href", next);
    anchor_0.setAttribute(FORWARDED_ATTR, "1");
  };
  const decorateAll = root => {
    if (root instanceof HTMLAnchorElement) {
      decorate(root);
    }
    root.querySelectorAll(CANDIDATE_SELECTOR).forEach(decorate);
    root.querySelectorAll(`a[${FORWARDED_ATTR}]`).forEach(decorate);
  };
  decorateAll(document.body);
  const observer = new MutationObserver(records => {
    for (const record of records) {
      if (record.type === "attributes") {
        const target = record.target;
        if (!(target instanceof HTMLAnchorElement)) {
          continue;
        }
        const current = target.getAttribute("href");
        if (target.getAttribute(FORWARDED_ATTR) === "1") {
          const original_0 = target.getAttribute(ORIGINAL_ATTR);
          const expected = original_0 ? withForwardedUtmParams(original_0, utm) : null;
          if (current === expected) {
            continue;
          }
          if (current && current !== expected) {
            target.setAttribute(ORIGINAL_ATTR, current);
          }
          target.removeAttribute(FORWARDED_ATTR);
        }
        decorate(target);
        continue;
      }
      record.addedNodes.forEach(node => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          decorateAll(node as Element);
        }
      });
    }
  });
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ["href"]
  });
  const onPointerDown = event => {
    const target_0 = event.target as Element | null;
    const anchor_1 = target_0?.closest?.("a");
    if (anchor_1 instanceof HTMLAnchorElement) {
      decorate(anchor_1);
    }
  };
  document.addEventListener("pointerdown", onPointerDown, true);
  return () => {
    observer.disconnect();
    document.removeEventListener("pointerdown", onPointerDown, true);
  };
}
function _temp(anchor) {
  const original = anchor.getAttribute(ORIGINAL_ATTR);
  if (original !== null) {
    anchor.setAttribute("href", original);
  }
  anchor.removeAttribute(ORIGINAL_ATTR);
  anchor.removeAttribute(FORWARDED_ATTR);
}